
import genderList from '@/resources/genderList';
import titlesList from '@/resources/titlesList';
import requestAPI from '@/logic/requestBeApi';
import store from '@/store';
import FileUploader from '@/components/Inputs/FileUploader.vue';
import config from '@/config';
import b64toBlob from '@/logic/b64toBlob';
import helpers from '@/logic/helpers/forms/onboardingHelpers';
import { getLocaleString } from '@/logic/requestBeApi';
import PhoneNumberInput from '@/components/Inputs/PhoneNumberInput.vue';
import CountryInput from '@/components/Inputs/CountryInput.vue';
import NationalityInput from '@/components/Inputs/NationalityInput.vue';
import inputValidators from '@/logic/helpers/inputs/validators';
import ServerResponseAlert from '@/components/ServerResponseAlert.vue';
import passDataToResponseAlert from '@/logic/helpers/api/passDataToResponseAlert';

export default {
  name: 'AssessorOnboardingBasics',

  components: {
    FileUploader,
    PhoneNumberInput,
    CountryInput,
    NationalityInput,
    ServerResponseAlert,
  },

  props: {
    isPartOfProfile: Boolean,
    basics: {
      type: Object,
      default: (): Record<string, never> => ({}),
    },
    email: {
      type: String,
      default: '',
    },
  },

  data(): Record<string, unknown> {
    return {
      showTitleSelect: false,
      disableFutureDates: {
        disabledDate(time: { getTime: () => number }) {
          return time.getTime() > Date.now();
        },
      },
      onboardingBasics: {
        email: '',
        title: '',
        first_name: '',
        initials: '',
        surname: '',
        gender: undefined,
        city: '',
        postal_code: '',
        country: '',
        nationality: '',
        date_of_birth: '',
        phone: '',
      },
      validationRules: {
        email: [{ validator: inputValidators.email, trigger: 'change' }],
        first_name: [{ validator: inputValidators.primitive, trigger: 'change' }],
        surname: [{ validator: inputValidators.primitive, trigger: 'change' }],
        gender: [{ validator: inputValidators.gender, trigger: 'change' }],
        city: [{ validator: inputValidators.primitive, trigger: 'change' }],
        postal_code: [{ validator: inputValidators.primitive, trigger: 'change' }],
        country: [{ validator: inputValidators.primitive, trigger: 'change' }],
        nationality: [{ validator: inputValidators.primitive, trigger: 'change' }],
        date_of_birth: [{ validator: inputValidators.primitive, trigger: 'change' }],
      },
      communicators: {
        skype: '',
        zoom: '',
        whereby: '',
      },
      titlesList,
      genderList,
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
      processingUserDataRequest: false,
      serverResponse: null,
      parseCV: false,
      parsingCV: false,
      saveAndQuit: false,
      formIsValid: false,
      contactDetailsId: '',
    };
  },

  computed: {
    country(): string {
      return this.onboardingBasics.country;
    },
  },

  watch: {
    country(): void {
      this.setUserCountryToStore();
    },

    basics(newVal: unknown): void {
      if (this.isPartOfProfile && newVal) {
        this.assignBasicData(newVal);
      }
    },

    email(newVal: string): void {
      this.onboardingBasics.email = newVal;
      store.commit('setUserEmail', newVal);
    },
  },

  mounted(): void {
    this.onboardingBasics.email = localStorage.getItem('email');
  },

  methods: {
    submitForm(saveOption: 'saveAndQuit' | 'saveAndContinue'): void {
      helpers.validateAndSubmitUserData(this, 'onboardingBasicsForm', saveOption);
    },

    setUserCountryToStore(): void {
      store.commit('setUserCountry', this.onboardingBasics.country);
    },

    assignBasicData(basics: {
      id: string;
      title: string;
      first_name: string;
      initials: string;
      surname: string;
      gender: string;
      address: { city: string; postal_code: string; country: string };
      nationality: string;
      date_of_birth: string;
      phones: string[];
      skype: string;
      zoom: string;
      whereby: string;
    }): void {
      if (!Object.keys(basics).length) return;

      this.contactDetailsId = basics.id;
      this.onboardingBasics.title = basics.title;
      this.onboardingBasics.first_name = basics.first_name;
      this.onboardingBasics.initials = basics.initials;
      this.onboardingBasics.surname = basics.surname;
      this.onboardingBasics.gender = basics.gender;
      this.onboardingBasics.city = basics.address.city;
      this.onboardingBasics.postal_code = basics.address.postal_code;
      this.onboardingBasics.country = basics.address.country;
      this.onboardingBasics.nationality = basics.nationality;
      this.onboardingBasics.date_of_birth = basics.date_of_birth;
      this.onboardingBasics.phone = basics?.phones[0];
      this.communicators.skype = basics.skype;
      this.communicators.zoom = basics.zoom;
      this.communicators.whereby = basics.whereby;
    },

    async submitUserData(): Promise<void> {
      this.serverResponse = {};
      this.processingUserDataRequest = true;

      if (this.isPartOfProfile) {
        const resp = await requestAPI({
          method: 'PATCH',
          contentType: 'application/merge-patch+json',
          url: '/api/v1/assessors/me/basic',
          payload: this.onboardingBasics,
        });

        const resp2 = await requestAPI({
          method: 'PATCH',
          contentType: 'application/merge-patch+json',
          url: `/api/v1/contact-details/${this.contactDetailsId}`,
          payload: this.communicators,
        });

        const allResponses = await Promise.all([resp, resp2]);

        if (allResponses[0].ok && allResponses[1].ok) {
          this.handleProfileBasicsResponse();
        } else if (allResponses[0].status !== 401) {
          const data = await passDataToResponseAlert(resp);
          this.serverResponse = data;
        } else if (allResponses[1].status !== 401) {
          const data = await passDataToResponseAlert(resp2);
          this.serverResponse = data;
        }

        this.processingUserDataRequest = false;
      } else {
        const resp = await requestAPI({
          method: 'POST',
          url: '/api/v1/onboard/basic',
          payload: this.onboardingBasics,
        });

        if (resp.ok) {
          this.saveAndQuit ? helpers.endOnboarding() : this.$emit('setStep', 'expertise', resp);
        } else {
          const data = await passDataToResponseAlert(resp);
          this.serverResponse = data;
          this.saveAndQuit = false;
        }

        this.processingUserDataRequest = false;
      }
    },

    handleProfileBasicsResponse(): void {
      this.serverResponse = {};

      this.$message.success(this.$t('yourInformationHasBeenUpdated'));

      if (this.email !== this.onboardingBasics.email) {
        window.location.reload();
      }

      this.$emit('runGetProfileCompletionProcess');
    },

    goNextScreen(): void {
      this.$emit('setStep', 'expertise');
    },

    informUserAboutCvParser(): void {
      this.$confirm(this.$t('cvParserWarning'), this.$t('cvParser'), {
        confirmButtonText: 'OK',
        confirmButtonClass: 'selenium--assessor-onboarding-basics__popup-confirm-btn',
        cancelButtonText: this.$t('cancel'),
        cancelButtonClass: 'selenium--assessor-onboarding-basics__popup-cancel-btn',
        type: 'warning',
      })
        .then(() => {
          this.parseCV = true;

          setTimeout(() => {
            // FIXME: Doesn't work on firefox for some reason
            (document.querySelector('#fileUploader') as HTMLElement).click();
          }, 100);
        })
        .catch((err: Error) => err);
    },

    createInitials(firstName: string, surname: string): string {
      if (!firstName || !surname) return '';

      return `${firstName.charAt(0).toUpperCase()}${surname.charAt(0).toUpperCase()}`;
    },

    createDateOfBirth(date: string): string {
      // Current format from BE is DD/MM/YYYY and we want YYYY-MM-DD
      if (/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(date)) return date;

      return date
        ?.split('/')
        .reverse()
        .join('-');
    },

    async handleFileUpload(payload: unknown): Promise<void> {
      this.parsingCV = true;

      const resp = await requestAPI({
        url: `/api/v1/cvs/parse`,
        method: 'POST',
        payload: {
          file: payload,
        },
      });

      if (resp.ok) {
        this.$message.success(this.$t('cvParsed'));

        const data = await resp.json();

        this.onboardingBasics.first_name = data.name ?? '';
        this.onboardingBasics.surname = data.surname ?? '';
        this.onboardingBasics.initials = this.createInitials(data.name, data.surname) ?? '';
        this.onboardingBasics.phone = data.phone ?? '';
        this.onboardingBasics.date_of_birth = this.createDateOfBirth(data.date_of_birth) ?? '';

        if (data.profile_image) await this.saveAsProfilePicture(data.profile_image);

        // Needed to provide suggestions on Company Details screen
        if (data.email) store.commit('setUserEmail', data.email);
        if (data.phone) store.commit('setUserPhone', { number: data.phone, type: 'personal' });

        this.highlightAutofilledInputs();
      } else if (resp.status !== 401) {
        this.$message({
          type: 'warning',
          message: this.$t('cvParsingFailed'),
          duration: 7000,
        });
      }

      this.parsingCV = false;
    },

    highlightAutofilledInputs(): void {
      const dataToFill = [
        { data: this.onboardingBasics.first_name, id: 'firstName' },
        { data: this.onboardingBasics.surname, id: 'surname' },
        { data: this.onboardingBasics.date_of_birth, id: 'birthDate' },
        { data: this.onboardingBasics.initials, id: 'initials' },
      ];

      dataToFill.forEach((input) => {
        if (input.data) {
          const elementToHighlight = document.getElementById(input.id);

          elementToHighlight.style.backgroundColor = '#e4ece7';
          elementToHighlight.style.backgroundImage =
            'radial-gradient(circle, #e4ece7 0%, #f3fff8 100%)';

          setTimeout(() => {
            elementToHighlight.style.backgroundColor = '#f3fff8';
            elementToHighlight.style.backgroundImage = 'none';
          }, 5000);
        }
      });
    },

    async saveAsProfilePicture(image: string): Promise<void> {
      const blob = b64toBlob(image, 'image');
      const formData = new FormData();

      formData.append('profile_picture', blob);

      let headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'X-LOCALE': getLocaleString(),
      };

      if (config.isOnDevEnv) delete headers['X-LOCALE'];

      const resp = await fetch(`${config.BE_URL}/api/v1/profile-pictures`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!resp.ok && resp.status !== 401)
        this.$message({
          type: 'warning',
          message: this.$t('profilePicParsingFail'),
          duration: 5000,
        });
    },

    showEmailChangeInfoModal(): void {
      if (!this.isPartOfProfile) return;

      this.$notify.warning({
        title: this.$t('important'),
        message: this.$t('emailChangeWarning'),
        duration: 20000,
        offset: 120,
      });
    },
  },
};
